import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  ArrowRight,
  CheckCircle2,
  Star,
  ShieldCheck,
  Wind,
  Building2,
  Clock,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import CalculatorCallout from "./CalculatorCallout";
import { cities, damageServices, damageUrl, cityUrl } from "../data/seoContent";
import { businessInfo, faqs, images, stats } from "../mock";
import {
  localBusinessSchema,
  faqSchema,
  breadcrumbSchema,
} from "../utils/schemas";

const CityPage = ({ citySlugOverride } = {}) => {
  const params = useParams();
  const slug = citySlugOverride || params.slug;
  const city = cities[slug];

  if (!city) return <Navigate to="/" replace />;

  const otherCities = Object.values(cities).filter((c) => c.slug !== slug);
  const damageList = Object.values(damageServices);

  const schemas = [
    localBusinessSchema({
      areaServed: [`${city.name} FL`],
      geo: city.geo,
      city: city.slug,
    }),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Service Areas", path: "/#service-areas" },
      { name: city.name, path: cityUrl(city.slug) },
    ]),
    faqSchema(city.cityFaqs || faqs),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={city.metaTitle}
        description={city.metaDescription}
        path={cityUrl(city.slug)}
        keywords={`public adjuster ${city.name} FL, insurance claim help ${city.name}, denied claim ${city.name}, hurricane claim ${city.name} FL`}
        schemas={schemas}
      />
      <Header />

      <Breadcrumbs
        items={[
          { name: "Service Areas", path: "/#service-areas" },
          { name: city.name, path: cityUrl(city.slug) },
        ]}
      />

      {/* Hero */}
      <section className="relative bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.tampa}
            alt={`${city.name}, FL Tampa Bay area`}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-12 w-12 rounded-full bg-[#E11D26]/20 border border-[#E11D26]/50 flex items-center justify-center text-[#E11D26]">
                <MapPin className="h-6 w-6" />
              </span>
              <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
                Public Adjuster · {city.name}, FL
                {city.isHQ && " · HQ"}
              </span>
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {city.headline}
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">
              {city.intro}
            </p>
            {city.neighborhoods && (
              <p className="mt-4 text-white/55 text-sm">
                <span className="text-[#E11D26] font-bold tracking-wider uppercase">
                  Serving:
                </span>{" "}
                {city.neighborhoods}
              </p>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#contact"
                className="group inline-flex items-center justify-between gap-6 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <span className="text-sm underline underline-offset-4">
                  Get a Free Claim Review
                </span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm">{businessInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats / trust bar */}
      <section className="border-y border-white/10 bg-black/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-3 divide-x divide-white/10">
          {stats.map((s, i) => (
            <div key={i} className="py-7 text-center">
              <div
                className="text-2xl sm:text-4xl font-black text-[#E11D26]"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                {s.value}
              </div>
              <div className="mt-1 text-white/70 text-[10px] sm:text-xs font-bold tracking-[0.3em]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why local */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <h2
              className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Why {city.name} Homeowners Choose{" "}
              <span className="text-[#E11D26]">Claim Beast</span>
            </h2>
            <p className="mt-5 text-white/75 leading-relaxed">
              We&apos;re the local public adjuster team {city.name} homeowners
              actually call by name. Same-day inspections, full claim
              documentation, and zero upfront cost — we only get paid when you
              do.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Local Riverview HQ — fast on-site response across " +
                  city.name,
                "Florida & Georgia licensed public adjusters",
                "No win, no fee — pure contingency pricing",
                "We handle every step from documentation to negotiation",
                "Experience with every major Florida insurance carrier",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                  <span className="text-white/85">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
            <TrustCard
              icon={<ShieldCheck className="h-6 w-6" />}
              title="Licensed"
              text="FL #W757540 · GA #3750441 · Firm #G283331"
            />
            <TrustCard
              icon={<Star className="h-6 w-6" />}
              title="4.9 Stars"
              text="182+ verified client reviews across Florida"
            />
            <TrustCard
              icon={<MapPin className="h-6 w-6" />}
              title="Local"
              text={`Riverview HQ · Minutes from ${city.name}`}
            />
            <TrustCard
              icon={<Phone className="h-6 w-6" />}
              title="24/7"
              text="Storm damage emergency? Call anytime."
            />
          </div>
        </div>
      </section>

      {/* Damage types in this city */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Every Type Of Claim In{" "}
            <span className="text-[#E11D26]">{city.name}</span>
          </h2>
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {damageList.map((d) => (
              <Link
                key={d.slug}
                to={damageUrl(d.slug)}
                className="bg-[#0F0F0F] hover:bg-[#141414] p-6 transition-colors group"
              >
                <div
                  className="text-white font-black uppercase text-lg tracking-wide group-hover:text-[#E11D26] transition-colors"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {d.title}
                </div>
                <div className="mt-2 text-white/55 text-xs">
                  In {city.name}, FL
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator callout — city-specific */}
      <CalculatorCallout
        eyebrow={`${city.name} Claim · 60-Second Estimate`}
        headline={`Wondering what your ${city.name} claim is worth?`}
        body={`Before you call, plug your damage into our ${city.name} claim calculator. It walks you through roof, water, wind, and interior damage line-by-line and returns a low–high estimate built from real Florida adjuster pricing — no contact info needed until you actually want the breakdown.`}
        ctaLabel={`Estimate My ${city.name} Claim`}
      />

      {city.extended && (
        <>
          {/* Extended Tampa intro */}
          <section className="py-16 md:py-20 bg-[#0A0A0A] border-y border-white/10">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-4">
                Local · Florida-Licensed · Contingency
              </div>
              <h2
                className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                What A {city.name} Public Adjuster Actually Does For You
              </h2>
              <p className="mt-6 text-white/80 leading-relaxed text-base sm:text-lg">
                {city.extendedIntro}
              </p>
              <p className="mt-4 text-white/65 leading-relaxed text-sm sm:text-base">
                {city.extendedSubtext}
              </p>
              <div className="mt-7 flex flex-wrap gap-3 text-xs">
                <Link
                  to="/denied-insurance-claim-florida"
                  className="bg-[#E11D26]/10 border border-[#E11D26]/40 text-white px-4 py-2 hover:bg-[#E11D26] transition-colors font-bold tracking-wider uppercase"
                  data-testid="tampa-link-denied"
                >
                  Denied Claim Help
                </Link>
                <Link
                  to="/underpaid-insurance-claim-florida"
                  className="bg-[#E11D26]/10 border border-[#E11D26]/40 text-white px-4 py-2 hover:bg-[#E11D26] transition-colors font-bold tracking-wider uppercase"
                  data-testid="tampa-link-underpaid"
                >
                  Underpaid Claim Help
                </Link>
                <Link
                  to="/roof-claim-denial-florida"
                  className="bg-[#E11D26]/10 border border-[#E11D26]/40 text-white px-4 py-2 hover:bg-[#E11D26] transition-colors font-bold tracking-wider uppercase"
                  data-testid="tampa-link-roof"
                >
                  Roof Claim Help
                </Link>
              </div>
            </div>
          </section>

          {/* Tampa-specific stats */}
          {city.localStats && (
            <section className="border-b border-white/10 bg-black">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-3 divide-x divide-white/10">
                {city.localStats.map((s, i) => (
                  <div key={i} className="py-7 text-center">
                    <div
                      className="text-2xl sm:text-4xl font-black text-[#E11D26]"
                      style={{
                        fontFamily: "'Anton', 'Oswald', sans-serif",
                      }}
                    >
                      {s.value}
                    </div>
                    <div className="mt-1 text-white/70 text-[10px] sm:text-xs font-bold tracking-[0.3em]">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Tampa neighborhoods grouped */}
          {city.neighborhoodGroups && (
            <section className="py-16 md:py-20 bg-[#0A0A0A]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
                  Every {city.name} Neighborhood
                </div>
                <h2
                  className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {city.name} Neighborhoods &amp;{" "}
                  <span className="text-[#E11D26]">ZIP Codes We Serve</span>
                </h2>
                <p className="mt-5 text-white/70 max-w-3xl">
                  We handle insurance claims in every {city.name}{" "}
                  neighborhood — from established communities to the newest
                  developments. Find your area below; if you don&apos;t see it,
                  call us — we serve every ZIP code across {city.name}.
                </p>
                <div className="mt-9 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {city.neighborhoodGroups.map((g, i) => (
                    <div key={i} className="bg-[#0F0F0F] p-6">
                      <div className="flex items-center gap-2 text-[#E11D26]">
                        <Building2 className="h-4 w-4" />
                        <span className="text-xs font-bold tracking-[0.25em] uppercase">
                          {g.area}
                        </span>
                      </div>
                      <div
                        className="mt-3 text-white text-sm font-bold tracking-wider"
                        style={{
                          fontFamily: "'Anton', 'Oswald', sans-serif",
                        }}
                      >
                        {g.zips}
                      </div>
                      <p className="mt-3 text-white/65 text-sm leading-relaxed">
                        {g.spots}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Recent Tampa storm history */}
          {city.recentStorms && (
            <section className="py-16 md:py-20 bg-black">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
                  Recent {city.name} Storm History
                </div>
                <h2
                  className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  The Storms That Shaped{" "}
                  <span className="text-[#E11D26]">{city.name} Claims</span>
                </h2>
                <p className="mt-5 text-white/70 max-w-3xl">
                  Many {city.name} homeowners still have unresolved or
                  underpaid claims from recent hurricanes. If you have damage
                  from any of these events that wasn&apos;t fully paid, your
                  claim may still be eligible to reopen — call us to find out.
                </p>
                <div className="mt-9 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {city.recentStorms.map((s, i) => (
                    <div key={i} className="bg-[#0F0F0F] p-6">
                      <div className="flex items-center gap-2 text-[#E11D26]">
                        <Wind className="h-5 w-5" />
                        <span
                          className="text-lg font-black uppercase tracking-wide"
                          style={{
                            fontFamily: "'Anton', 'Oswald', sans-serif",
                          }}
                        >
                          {s.name}
                        </span>
                      </div>
                      <div className="mt-2 text-white/55 text-xs flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        {s.date}
                      </div>
                      <p className="mt-4 text-white/75 text-sm leading-relaxed">
                        {s.impact}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link
                    to="/blog/florida-hurricane-claim-deadlines-explained"
                    className="inline-flex items-center gap-2 text-[#E11D26] hover:text-white text-sm font-bold tracking-wider uppercase transition-colors"
                    data-testid="tampa-link-hurricane-deadlines"
                  >
                    Read: Florida Hurricane Claim Deadlines (2026 Update)
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* City-specific FAQs */}
          {city.cityFaqs && (
            <section className="py-16 md:py-20 bg-[#0A0A0A] border-t border-white/10">
              <div className="max-w-4xl mx-auto px-4 sm:px-6">
                <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
                  {city.name} Homeowner FAQs
                </div>
                <h2
                  className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  Questions {city.name} Homeowners{" "}
                  <span className="text-[#E11D26]">Ask Us Most</span>
                </h2>
                <div className="mt-9 divide-y divide-white/10 border-y border-white/10">
                  {city.cityFaqs.map((f, i) => (
                    <details
                      key={i}
                      className="group py-5"
                      data-testid={`tampa-faq-${i}`}
                    >
                      <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                        <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                          {f.question}
                        </h3>
                        <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                          +
                        </span>
                      </summary>
                      <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                        {f.answer}
                      </p>
                    </details>
                  ))}
                </div>
                <div className="mt-9 flex flex-wrap gap-4 text-sm">
                  <Link
                    to="/blog/why-insurance-companies-deny-roof-claims-in-florida"
                    className="text-[#E11D26] hover:text-white font-bold tracking-wider uppercase transition-colors inline-flex items-center gap-2"
                    data-testid="tampa-link-blog-roof"
                  >
                    Why Carriers Deny Roof Claims
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/blog/public-adjuster-vs-attorney-which-should-you-hire-first"
                    className="text-[#E11D26] hover:text-white font-bold tracking-wider uppercase transition-colors inline-flex items-center gap-2"
                    data-testid="tampa-link-blog-pa-vs-atty"
                  >
                    Public Adjuster vs Attorney
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/blog/how-to-appeal-a-denied-property-insurance-claim-in-florida"
                    className="text-[#E11D26] hover:text-white font-bold tracking-wider uppercase transition-colors inline-flex items-center gap-2"
                    data-testid="tampa-link-blog-appeal"
                  >
                    How To Appeal a Denied Claim
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/blog/how-to-appeal-an-insurance-claim-denial-in-florida"
                    className="text-[#E11D26] hover:text-white font-bold tracking-wider uppercase transition-colors inline-flex items-center gap-2"
                    data-testid="tampa-link-blog-appeal-denial"
                  >
                    Appeal an Insurance Claim Denial
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/blog/insurance-underpayment-recovery-florida-how-it-works"
                    className="text-[#E11D26] hover:text-white font-bold tracking-wider uppercase transition-colors inline-flex items-center gap-2"
                    data-testid="tampa-link-blog-underpayment"
                  >
                    Insurance Underpayment Recovery
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Flagship Tampa authority link — appears on every city page */}
      <section className="py-12 bg-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Link
            to="/public-adjuster-tampa"
            data-testid="city-to-tampa-flagship"
            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-5 bg-[#0F0F0F] border border-[#E11D26]/40 hover:border-[#E11D26] hover:bg-[#1a0709] p-6 sm:p-8 transition-colors"
          >
            <div className="flex items-start gap-4">
              <MapPin className="h-7 w-7 text-[#E11D26] shrink-0 mt-1" />
              <div>
                <div className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                  Serving The Tampa Bay Area
                </div>
                <h2
                  className="mt-1 text-white text-xl sm:text-2xl font-black uppercase leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  Looking For The Best Public Adjuster In Tampa, FL?
                </h2>
                <p className="mt-1 text-white/65 text-sm max-w-2xl">
                  See our flagship Tampa page — case studies, your rights under
                  Florida law, and how we win denied &amp; underpaid claims.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 text-white font-bold tracking-wider uppercase text-sm shrink-0">
              View Tampa Page
              <ArrowRight className="h-5 w-5 text-[#E11D26] transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        </div>
      </section>

      {/* Other cities */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Public Adjuster In{" "}
            <span className="text-[#E11D26]">Other Tampa Bay Cities</span>
          </h2>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={cityUrl(c.slug)}
                className="bg-[#0F0F0F] hover:bg-[#E11D26] hover:text-white transition-colors p-4 text-white/85 text-sm font-bold tracking-wide uppercase flex items-center gap-2"
              >
                <MapPin className="h-4 w-4 text-[#E11D26] group-hover:text-white shrink-0" />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {city.name} homeowners — start your free claim review now.
          </h3>
          <Link
            to="/#contact"
            className="group inline-flex items-center gap-3 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            <span className="text-sm">Free Review</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const TrustCard = ({ icon, title, text }) => (
  <div className="bg-[#0F0F0F] p-6">
    <div className="h-12 w-12 rounded-full bg-[#E11D26]/15 border border-[#E11D26]/40 flex items-center justify-center text-[#E11D26]">
      {icon}
    </div>
    <div
      className="mt-4 text-white text-xl font-black uppercase tracking-wide"
      style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
    >
      {title}
    </div>
    <p className="mt-2 text-white/60 text-sm leading-relaxed">{text}</p>
  </div>
);

export default CityPage;
